/**
 * Vehicles hitting vehicles: how a hit is shared by how heavy each is.
 *
 * Along the line of the hit, momentum is kept and a little of the closing
 * speed comes back as a rebound (`RESTITUTION`, about a tenth: car crashes
 * are nearly plastic, the energy going into the crushing). So each vehicle's
 * change in velocity is the shared impulse over its own mass
 * (`exchange`): a hatchback into a parked lorry stops dead and the lorry
 * barely stirs; a lorry into a parked hatchback hardly slows and throws it
 * across the street. A wall or a tree is a body of infinite mass. Hit off its
 * middle, a vehicle spins (`spinFrom`). Then it slides (`slide`): a parked car
 * on its handbrake and in gear, a traffic car with its driver standing on
 * the brakes, scrubbing off speed and spin on its tyres until it stops.
 */

/** How heavy each kind is, kg: kerb weight, a lorry about half laden. */
export const MASS: Record<string, number> = {
  carHatch: 1150,
  carSedan: 1400,
  carEstate: 1450,
  carSuv: 1900,
  carVan: 1800,
  carPickup: 2000,
  parkedMoto: 110,
  parkedScooter: 24,
  truckBox: 11000,
  // Tractor and trailer together.
  truckSemi: 20000,
};

/** A rider on a motorbike, kg; a driver in a car is lost in its weight. */
export const RIDER = 80;

/** How heavy a vehicle of `species` is, kg; with `ridden`, its rider too. */
export function massOf(species: string, ridden = false): number {
  const mass = MASS[species] ?? 1400;
  return (species === 'parkedMoto' || species === 'parkedScooter') && ridden ? mass + RIDER : mass;
}

/** The share of the closing speed a crash gives back as a rebound. */
export const RESTITUTION = 0.1;

/**
 * The hit between a body of mass `m1` closing on one of `m2` at `closing`
 * m/s along the line between them: each one's change in velocity along
 * that line, m/s, and the impulse, N·s. `m2` may be Infinity: a wall.
 * Nothing when they are not closing.
 */
export function exchange(m1: number, m2: number, closing: number, restitution = RESTITUTION): { dv1: number; dv2: number; impulse: number } {
  if (closing <= 0 || !(m1 > 0)) return { dv1: 0, dv2: 0, impulse: 0 };
  const inverse = 1 / m1 + (Number.isFinite(m2) && m2 > 0 ? 1 / m2 : 0);
  const impulse = ((1 + restitution) * closing) / inverse;
  return { dv1: impulse / m1, dv2: Number.isFinite(m2) ? impulse / m2 : 0, impulse };
}

/**
 * How fast a vehicle of `mass`, `halfLength` by `halfWidth`, starts to spin
 * when struck with `impulse` (scene x, z, N·s) at `arm` from its middle:
 * rad/s, positive turning from +x toward +z. A box's moment of inertia, as a
 * car is near enough.
 */
export function spinFrom(mass: number, halfLength: number, halfWidth: number, arm: { x: number; z: number }, impulse: { x: number; z: number }): number {
  const inertia = (mass * ((2 * halfLength) ** 2 + (2 * halfWidth) ** 2)) / 12;
  return (arm.x * impulse.z - arm.z * impulse.x) / inertia;
}

/** A vehicle sliding after a hit, scene frame: velocity, m/s, and spin, rad/s. */
export interface Sliding {
  vx: number;
  vz: number;
  spin: number;
}

/** How hard a parked car, braked and in gear, scrubs to a stop, m/s²; a driver standing on the brakes. */
export const PARKED_DRAG = 6;
export const BRAKING_DRAG = 7.5;

/**
 * Slide on by `dt` seconds, slowing at `drag` m/s² and spin falling off as
 * the tyres scrub. Returns how far it moved and turned. Stopped, it is all
 * zeros: nothing slides backward out of friction.
 */
export function slide(s: Sliding, dt: number, drag: number): { dx: number; dz: number; turn: number } {
  const speed = Math.hypot(s.vx, s.vz);
  // Snapped to a stop at the last crawl, so stopped is stopped.
  const left = speed - drag * dt;
  const slower = left < 0.02 ? 0 : left;
  const k = speed > 1e-6 ? slower / speed : 0;
  // Average over the step, so a stop is where friction leaves it.
  const dx = ((s.vx + s.vx * k) / 2) * dt, dz = ((s.vz + s.vz * k) / 2) * dt;
  s.vx *= k;
  s.vz *= k;
  // Spin dies away on the tyres: by friction and in proportion.
  const kept = Math.max(0, Math.abs(s.spin) - 2.5 * dt) * Math.exp(-dt * 1.2);
  const spin = kept < 0.01 ? 0 : Math.sign(s.spin) * kept;
  const turn = ((s.spin + spin) / 2) * dt;
  s.spin = spin;
  return { dx, dz, turn };
}

/** Whether a slide has come to a stop. */
export function stopped(s: Sliding): boolean {
  return Math.hypot(s.vx, s.vz) < 0.02 && Math.abs(s.spin) < 0.01;
}

/**
 * How stiff each build is, against a car's 1: a lorry's frame and cab
 * give a third as much under the same force, a motorbike's forks and
 * plastics more than a car's crumple zone.
 */
export const STIFFNESS: Record<'car' | 'truck' | 'moto', number> = { car: 1, truck: 3, moto: 0.6 };

/**
 * How hard each of two vehicles is crushed by a hit: its equivalent barrier
 * speed, m/s, the speed into a rigid wall that would crush it as much.
 * The energy the hit takes out of their closing, ½ μ v² (1 − e²) for their
 * reduced mass μ, goes into crushing the two structures as into two springs
 * in series: each takes a share inverse to its stiffness. Then each one's
 * share over its own mass gives its speed: √(2 E / m). A hatchback into a
 * lorry takes nearly all of it; the lorry hardly any. Into a wall (m2
 * Infinity) it is the closing speed itself.
 */
export function crushSpeeds(m1: number, m2: number, closing: number, k1: number, k2: number, restitution = RESTITUTION): { first: number; second: number } {
  if (closing <= 0) return { first: 0, second: 0 };
  if (!Number.isFinite(m2)) return { first: closing * Math.sqrt(1 - restitution * restitution), second: 0 };
  const reduced = (m1 * m2) / (m1 + m2);
  const energy = 0.5 * reduced * closing * closing * (1 - restitution * restitution);
  const share1 = (1 / k1) / (1 / k1 + 1 / k2);
  return { first: Math.sqrt((2 * energy * share1) / m1), second: Math.sqrt((2 * energy * (1 - share1)) / m2) };
}

/** A vehicle's footprint: its middle (scene x, z), unit heading, and half its length and width. */
export interface Footprint {
  x: number;
  z: number;
  dx: number;
  dz: number;
  halfLength: number;
  halfWidth: number;
}

/**
 * Whether two footprints overlap, by the separating axis test on their four
 * edge directions. If they do: how deep, the unit normal to push `b` out of
 * `a` along (scene x, z), and a point where they touch (the deepest corner).
 */
export function overlap(a: Footprint, b: Footprint): { depth: number; nx: number; nz: number; x: number; z: number } | undefined {
  const axes: Array<[number, number]> = [[a.dx, a.dz], [-a.dz, a.dx], [b.dx, b.dz], [-b.dz, b.dx]];
  const rx = b.x - a.x, rz = b.z - a.z;
  let depth = Infinity, nx = 0, nz = 0;
  for (const [ux, uz] of axes) {
    // Each box's half extent along the axis, and how far apart their middles are on it.
    const ea = Math.abs(a.dx * ux + a.dz * uz) * a.halfLength + Math.abs(-a.dz * ux + a.dx * uz) * a.halfWidth;
    const eb = Math.abs(b.dx * ux + b.dz * uz) * b.halfLength + Math.abs(-b.dz * ux + b.dx * uz) * b.halfWidth;
    const apart = rx * ux + rz * uz;
    const d = ea + eb - Math.abs(apart);
    if (d <= 0) return undefined;
    if (d < depth) {
      depth = d;
      const s = apart >= 0 ? 1 : -1;
      nx = ux * s; nz = uz * s;
    }
  }
  // The corner of `b` deepest into `a` along the normal: where they meet.
  let best = Infinity, cx = b.x, cz = b.z;
  for (const sl of [-1, 1]) {
    for (const sw of [-1, 1]) {
      const x = b.x + b.dx * b.halfLength * sl - b.dz * b.halfWidth * sw;
      const z = b.z + b.dz * b.halfLength * sl + b.dx * b.halfWidth * sw;
      const along = (x - a.x) * nx + (z - a.z) * nz;
      if (along < best) { best = along; cx = x; cz = z; }
    }
  }
  return { depth, nx, nz, x: cx, z: cz };
}

/**
 * A motorbike falling over: how far over it is (radians, 0 upright), and how
 * fast it is going over. It falls as a stick from its wheels, faster the
 * further over, until it lies on its side, propped up a little by its bars
 * and pegs (`FALLEN`).
 */
export interface Fall {
  angle: number;
  rate: number;
}

/** How far over a fallen motorbike lies, radians: on its side, propped by its bars and pegs. */
export const FALLEN = 1.33;
/** How high a bike's weight sits, metres: the stick it falls as. */
const BIKE_HEIGHT = 0.55;

/** Go on falling by `dt`. True once it lies on its side. */
export function fall(f: Fall, dt: number): boolean {
  if (f.angle >= FALLEN) return true;
  // An inverted pendulum: θ'' = (3g / 2h) sin θ for a stick pivoting at its foot.
  f.rate += ((3 * 9.81) / (2 * BIKE_HEIGHT)) * Math.sin(Math.max(0.02, f.angle)) * dt;
  f.angle = Math.min(FALLEN, f.angle + f.rate * dt);
  if (f.angle >= FALLEN) f.rate = 0;
  return f.angle >= FALLEN;
}

/** How far from footprint `f` a point (x, z) is, metres: 0 inside it. */
export function clearance(f: Footprint, x: number, z: number): number {
  const rx = x - f.x, rz = z - f.z;
  const along = Math.abs(rx * f.dx + rz * f.dz) - f.halfLength;
  const across = Math.abs(-rx * f.dz + rz * f.dx) - f.halfWidth;
  return Math.hypot(Math.max(0, along), Math.max(0, across));
}

/**
 * How far along a road ahead (x, z pairs every `step` metres from a car's
 * middle, `count` of them) a car `halfWidth` wide would first touch
 * footprint `f`, give or take `margin`: metres, or Infinity if it does not.
 * The distance is to the stretch of road between points, not just to them.
 * Only from `from` metres on: from its nose, not what is beside it now.
 */
export function firstOnPath(path: ArrayLike<number>, count: number, step: number, halfWidth: number, f: Footprint, margin: number, from = 0): number {
  for (let k = Math.ceil(from / step); k < count; k++) {
    const x = path[k * 2], z = path[k * 2 + 1];
    if (clearance(f, x, z) < halfWidth + margin) return k * step;
    if (k + 1 < count && clearance(f, (x + path[k * 2 + 2]) / 2, (z + path[k * 2 + 3]) / 2) < halfWidth + margin) return (k + 0.5) * step;
  }
  return Infinity;
}

/**
 * Where a car's road ahead (`path`, as for `firstOnPath`) crosses the way
 * another vehicle is heading: that one at (x, z) going (dx, dz) at `speed`,
 * looked ahead `seconds`. How far along its own road the car is when they
 * meet, and how far the other has to go to get there, metres; undefined if
 * their ways do not cross within reach. `reach` is how close counts: both
 * half-widths and a margin.
 */
export function conflict(
  path: ArrayLike<number>, count: number, step: number, from: number,
  x: number, z: number, dx: number, dz: number, speed: number, seconds: number, reach: number,
): { mine: number; theirs: number } | undefined {
  const length = Math.max(4, speed * seconds);
  for (let k = Math.ceil(from / step); k < count; k++) {
    const px = path[k * 2] - x, pz = path[k * 2 + 1] - z;
    const along = px * dx + pz * dz;
    if (along < -1 || along > length) continue;
    if (Math.abs(px * dz - pz * dx) < reach) return { mine: k * step, theirs: Math.max(0, along) };
  }
  return undefined;
}

/**
 * Who gives way where two vehicles' ways cross: the one that would get there
 * later, if they would be there within `GAP` seconds of each other; when
 * both would at once, the one with the other on its right (högerregeln).
 * `mine` and `theirs` are seconds to the crossing; true if this one waits.
 */
export function yields(mine: number, theirs: number, otherOnRight: boolean): boolean {
  if (Math.abs(mine - theirs) > CROSSING_GAP) return false;
  if (Math.abs(mine - theirs) < 0.3) return otherOnRight;
  return mine > theirs;
}

/** Arriving this many seconds apart, two vehicles' ways may cross safely. */
export const CROSSING_GAP = 2;
